/**
 * REVIEW REQUESTS PAGE
 *
 * Platform Team: View and review pending onboarding and deactivation requests
 */

import { DeactivationCard } from "@/components/deactivation-card";
import { OrganizationCard } from "@/components/organization-card";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { createClient } from "@/lib/supabase";
import { canAccessPlatformWork } from "@/lib/team-access";
import type { LocationDeactivationQueue, Organization } from "@/types/database";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Search,
  XCircle
} from "lucide-react";
import { useEffect, useState } from "react";

type ExtendedOrganization = Organization & {
  locations?: Array<{ id: string; name: string; description?: string }>;
  rejection_reason?: string | null;
  review_notes?: string | null;
};

export default function ReviewPage() {
  const { user, loading: authLoading } = useAuth();
  const [pendingOrganizations, setPendingOrganizations] = useState<
    ExtendedOrganization[]
  >([]);
  const [approvedOrganizations, setApprovedOrganizations] = useState<
    ExtendedOrganization[]
  >([]);
  const [rejectedOrganizations, setRejectedOrganizations] = useState<
    ExtendedOrganization[]
  >([]);
  const [pendingDeactivations, setPendingDeactivations] = useState<
    (LocationDeactivationQueue & {
      organization?: { name: string; logo_url?: string | null };
    })[]
  >([]);
  const [approvedDeactivations, setApprovedDeactivations] = useState<
    (LocationDeactivationQueue & {
      organization?: { name: string; logo_url?: string | null };
    })[]
  >([]);
  const [completedDeactivations, setCompletedDeactivations] = useState<
    (LocationDeactivationQueue & {
      organization?: { name: string; logo_url?: string | null };
    })[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [mode, setMode] = useState<"onboarding" | "deactivation">("onboarding");
  const [searchQuery, setSearchQuery] = useState("");

  // Helper function to map queue data to Organization type
  const mapQueueToOrganization = (
    queue: any,
    locationsMap: Map<string, any[]>
  ): ExtendedOrganization => {
    const locationsArray = locationsMap.get(queue.id) || [];
    return {
      id: queue.id,
      name: queue.organization_name,
      slug: "", // Not used, keeping for type compatibility
      description: queue.organization_description,
      logo_url: queue.organization_logo,
      primary_contact_name: queue.primary_contact_name || null,
      primary_contact_email: queue.primary_contact_email || null,
      primary_contact_phone: null,
      status: queue.status,
      reviewed_by: null,
      reviewed_at: null,
      review_notes: queue.review_notes || null,
      rejection_reason: queue.rejection_reason || null,
      external_org_id: null,
      payment_link: null,
      payment_received_at: null,
      metadata: {},
      created_at: queue.created_at,
      updated_at: queue.updated_at,
      activated_at: null,
      locations: locationsArray.map((loc: any) => ({
        id: loc.id,
        name: loc.location_name,
        description: loc.location_description || null,
      })),
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      // Check role authorization
      if (!canAccessPlatformWork(user.role)) {
        return;
      }

      try {
        setLoading(true);
        const supabase = createClient();

        // Fetch pending reviews
        const { data: pendingQueue, error: pendingError } = await supabase
          .from("onboarding_queue")
          .select("*")
          .eq("status", "PENDING_REVIEW")
          .order("created_at", { ascending: false });

        if (pendingError) throw pendingError;

        // Fetch approved reviews
        const { data: approvedQueue, error: approvedError } = await supabase
          .from("onboarding_queue")
          .select("*")
          .eq("status", "APPROVED")
          .order("created_at", { ascending: false });

        if (approvedError) throw approvedError;

        // Fetch rejected reviews
        const { data: rejectedQueue, error: rejectedError } = await supabase
          .from("onboarding_queue")
          .select("*")
          .eq("status", "REJECTED")
          .order("created_at", { ascending: false });

        if (rejectedError) throw rejectedError;

        // Fetch all locations for these organizations
        const allQueues = [
          ...(pendingQueue || []),
          ...(approvedQueue || []),
          ...(rejectedQueue || []),
        ];
        const orgIds = allQueues.map((q) => q.id);

        const { data: locationsData, error: locationsError } = await supabase
          .from("onboarding_locations_queue")
          .select("*")
          .in("onboarding_queue_id", orgIds);

        if (locationsError) throw locationsError;

        // Build a map of organization ID to locations
        const locationsMap = new Map<string, any[]>();
        (locationsData || []).forEach((loc) => {
          if (!locationsMap.has(loc.onboarding_queue_id)) {
            locationsMap.set(loc.onboarding_queue_id, []);
          }
          locationsMap.get(loc.onboarding_queue_id)!.push(loc);
        });

        setPendingOrganizations(
          pendingQueue?.map((q) => mapQueueToOrganization(q, locationsMap)) ||
            []
        );
        setApprovedOrganizations(
          approvedQueue?.map((q) => mapQueueToOrganization(q, locationsMap)) ||
            []
        );
        setRejectedOrganizations(
          rejectedQueue?.map((q) => mapQueueToOrganization(q, locationsMap)) ||
            []
        );

        // Fetch deactivation requests
        const {
          data: pendingDeactivationQueue,
          error: pendingDeactivationError,
        } = await supabase
          .from("location_deactivation_queue")
          .select(
            `
            *,
            organization:organizations(name, logo_url)
          `
          )
          .eq("status", "PENDING_APPROVAL")
          .order("created_at", { ascending: false });

        if (pendingDeactivationError) {
          console.error(
            "Error fetching pending deactivations:",
            pendingDeactivationError
          );
        }

        const {
          data: approvedDeactivationQueue,
          error: approvedDeactivationError,
        } = await supabase
          .from("location_deactivation_queue")
          .select(
            `
            *,
            organization:organizations(name, logo_url)
          `
          )
          .in("status", [
            "APPROVED",
            "AWAITING_IT_DECONFIG",
            "AWAITING_DATA_DECONFIG",
            "AWAITING_DEACTIVATION",
          ])
          .order("created_at", { ascending: false });

        if (approvedDeactivationError) {
          console.error(
            "Error fetching approved deactivations:",
            approvedDeactivationError
          );
        }

        const {
          data: completedDeactivationQueue,
          error: completedDeactivationError,
        } = await supabase
          .from("location_deactivation_queue")
          .select(
            `
            *,
            organization:organizations(name, logo_url)
          `
          )
          .eq("status", "DEACTIVATED")
          .order("created_at", { ascending: false });

        if (completedDeactivationError) {
          console.error(
            "Error fetching completed deactivations:",
            completedDeactivationError
          );
        }

        setPendingDeactivations((pendingDeactivationQueue as any) || []);
        setApprovedDeactivations((approvedDeactivationQueue as any) || []);
        setCompletedDeactivations((completedDeactivationQueue as any) || []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Unknown error"));
        console.error("Error fetching review data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-red-600 dark:text-red-400">
              Error loading reviews: {error.message}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (
    !user ||
    (user.role !== "PLATFORM_TEAM" &&
      user.role !== "PLATFORM_ADMIN" &&
      user.role !== "SYSTEM_ADMIN")
  ) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Review Requests
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Review and approve onboarding and deactivation requests
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          type="text"
          placeholder="Search organizations..."
          className="pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-800">
        <button
          onClick={() => setMode("onboarding")}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            mode === "onboarding"
              ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          }`}
        >
          <span>Onboarding Requests</span>
          {pendingOrganizations.length > 0 && (
            <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              {pendingOrganizations.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setMode("deactivation")}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            mode === "deactivation"
              ? "border-b-2 border-red-600 text-red-600 dark:text-red-400"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          }`}
        >
          <span>Deactivation Requests</span>
          {pendingDeactivations.length > 0 && (
            <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              {pendingDeactivations.length}
            </span>
          )}
        </button>
      </div>

      {mode === "onboarding" && (
        <OnboardingRequestsContent
          pending={pendingOrganizations.filter((org) =>
            org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            org.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            org.primary_contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            org.primary_contact_email?.toLowerCase().includes(searchQuery.toLowerCase())
          )}
          approved={approvedOrganizations.filter((org) =>
            org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            org.description?.toLowerCase().includes(searchQuery.toLowerCase())
          )}
          rejected={rejectedOrganizations.filter((org) =>
            org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            org.description?.toLowerCase().includes(searchQuery.toLowerCase())
          )}
        />
      )}
      {mode === "deactivation" && (
        <DeactivationRequestsContent
          pending={pendingDeactivations.filter((deactivation) =>
            deactivation.location_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            deactivation.location_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            deactivation.organization?.name.toLowerCase().includes(searchQuery.toLowerCase())
          )}
          approved={approvedDeactivations.filter((deactivation) =>
            deactivation.location_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            deactivation.location_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            deactivation.organization?.name.toLowerCase().includes(searchQuery.toLowerCase())
          )}
          completed={completedDeactivations.filter((deactivation) =>
            deactivation.location_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            deactivation.location_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            deactivation.organization?.name.toLowerCase().includes(searchQuery.toLowerCase())
          )}
        />
      )}
    </div>
  );
}

function OnboardingRequestsContent({
  pending,
  approved,
  rejected,
}: {
  pending: ExtendedOrganization[];
  approved: ExtendedOrganization[];
  rejected: ExtendedOrganization[];
}) {
  return (
    <div className="space-y-6">
      {/* Empty State */}
      {pending.length === 0 &&
      approved.length === 0 &&
      rejected.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardCheck className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              No onboarding requests yet
            </h3>
            <p className="text-muted-foreground text-center max-w-md">
              Onboarding requests will appear here once organizations submit
              their applications.
            </p>
          </CardContent>
        </Card>
      ) : (
        /* Kanban Board */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-280px)] min-h-[500px] max-h-[calc(100vh-280px)] overflow-hidden">
          {/* Pending Column */}
          <div className="flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  Pending Review
                </h2>
              </div>
              <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full px-2.5 py-1 text-sm font-medium">
                {pending.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500">
              {pending.map((org) => (
                <OrganizationCard
                  key={org.id}
                  organization={org as any}
                  href={`/review/${org.id}`}
                />
              ))}
            </div>
          </div>

          {/* Approved Column */}
          <div className="flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  Approved
                </h2>
              </div>
              <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full px-2.5 py-1 text-sm font-medium">
                {approved.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500">
              {approved.map((org) => (
                <OrganizationCard
                  key={org.id}
                  organization={org as any}
                  href={`/review/${org.id}`}
                  showDetails={false}
                />
              ))}
            </div>
          </div>

          {/* Rejected Column */}
          <div className="flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  Rejected
                </h2>
              </div>
              <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full px-2.5 py-1 text-sm font-medium">
                {rejected.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500">
              {rejected.map((org) => (
                <OrganizationCard
                  key={org.id}
                  organization={org as any}
                  href={`/review/${org.id}`}
                  showDetails={false}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeactivationRequestsContent({
  pending,
  approved,
  completed,
}: {
  pending: (LocationDeactivationQueue & {
    organization?: { name: string; logo_url?: string | null };
  })[];
  approved: (LocationDeactivationQueue & {
    organization?: { name: string; logo_url?: string | null };
  })[];
  completed: (LocationDeactivationQueue & {
    organization?: { name: string; logo_url?: string | null };
  })[];
}) {
  return (
    <div className="space-y-6">
      {/* Empty State */}
      {pending.length === 0 &&
      approved.length === 0 &&
      completed.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              No deactivation requests yet
            </h3>
            <p className="text-muted-foreground text-center max-w-md">
              Deactivation requests will appear here once locations are
              submitted for deactivation.
            </p>
          </CardContent>
        </Card>
      ) : (
        /* Kanban Board */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-280px)] min-h-[500px] max-h-[calc(100vh-280px)] overflow-hidden">
          {/* Pending Column */}
          <div className="flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  Pending Approval
                </h2>
              </div>
              <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full px-2.5 py-1 text-sm font-medium">
                {pending.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500">
              {pending.map((deactivation) => (
                <DeactivationCard
                  key={deactivation.id}
                  deactivation={deactivation}
                  href={`/review/deactivation/${deactivation.id}`}
                />
              ))}
            </div>
          </div>

          {/* Approved Column */}
          <div className="flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  Approved
                </h2>
              </div>
              <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full px-2.5 py-1 text-sm font-medium">
                {approved.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500">
              {approved.map((deactivation) => (
                <DeactivationCard
                  key={deactivation.id}
                  deactivation={deactivation}
                  href={`/review/deactivation/${deactivation.id}`}
                  showDetails={false}
                />
              ))}
            </div>
          </div>

          {/* Completed Column */}
          <div className="flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  Completed
                </h2>
              </div>
              <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full px-2.5 py-1 text-sm font-medium">
                {completed.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500">
              {completed.map((deactivation) => (
                <DeactivationCard
                  key={deactivation.id}
                  deactivation={deactivation}
                  href={`/review/deactivation/${deactivation.id}`}
                  showDetails={false}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
